import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Icon from '@material-ui/core/Icon/Icon';
import RootRef from '@material-ui/core/RootRef/RootRef';
import Notification from '@configuratorware/configurator-frontendgui/App/Shared/Components/Notification';
import AddToCart from '@configuratorware/configurator-frontendgui/App/Shared/Components/AddToBasket';
import LoadingOverlay from '@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay';
import isEqual from 'lodash/isEqual';
import { MATERIAL, PRODUCT, DESIGN } from '../../Constants/Steps';
import BackStepLink from '../BackStepLink/BackStepLink';
import ReceiveOfferForm from '../ReceiveOfferForm/ReceiveOfferForm';
import AcceptDesign from '../AcceptDesign/AcceptDesign';
import { isEmpty } from 'lodash';
import PriceOverview from '../PriceOverview/PriceOverview';
import FeedbackSuccessDialog from './FeedbackSuccessDialog';

const styles = (theme) => {
  const buttons = {
    mainBtn: {},
    mainBtnHover: {},
    ...theme.buttons,
  };
  return {
    root: {
      [theme.breakpoints.between('xs', 'sm')]: {
        display: 'flex',
        flex: 1,
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        position: 'relative',
      },
      [theme.breakpoints.up('md')]: {
        minWidth: '50%',
        maxWidth: '75%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      },
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(1),
      },
    },
    AmountPrice: {
      [theme.breakpoints.down('xs')]: {
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'column',
        marginLeft: 20,
      },
    },
    AmountPriceContent: {
      padding: '0px 0px 5px',
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexDirection: 'row',
      },
      [theme.breakpoints.up('md')]: { marginBottom: 16 },
    },
    secondaryButton: {
      backgroundColor: '#818181 !important',
      color: '#ffffff',
      marginBottom: '12px !important',
    },
    mainButton: {
      margin: 0,
      padding: '6px 15px',
      height: 50,
      borderRadius: 0,
      lineHeight: 1.5,
      ...buttons.mainBtn,
      '&.MuiButton-contained.Mui-disabled': {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.4,
      },
      '&:hover': {
        ...buttons.mainBtnHover,
        cursor: 'pointer',
      },
      '@media (hover: none)': {
        '&:hover': {
          ...buttons.mainBtn,
          opacity: 1,
        },
      },
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    mainButtonLabel: {
      color: 'white',
      fontSize: 15,
      fontWeight: 'bold',
      textTransform: 'none',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        whiteSpace: 'normal',
      },
    },
    AmountPriceTypography: {
      textAlign: 'end',
      lineHeight: 1.2,
      [theme.breakpoints.down('xs')]: {
        padding: '3px 0',
        textAlign: 'start',
        fontSize: 20,
        lineHeight: 1,
      },
    },
    AmountPriceTypographyVat: {
      fontSize: 12,
      lineHeight: 1.2,
      textAlign: 'end',
      display: 'block',

      [theme.breakpoints.down('xs')]: {
        textAlign: 'start',
        fontSize: 8,
      },
    },
    contentPositioning: {
      paddingLeft: 32,
      paddingRight: 20,
      [theme.breakpoints.down('xs')]: {
        paddingLeft: 0,
        paddingRight: 8,
        marginRight: '-8px',
      },
      '& p': {
        letterSpacing: 'normal',
      },
      marginRight: 4,
      padding: 0,
    },
    dialogWrapper: {
      borderTop: '7px solid ' + theme.palette.primary.main,
      padding: '0 32px',
      overflow: 'hidden',
      [theme.breakpoints.down('xs')]: {
        padding: '0 16px',
      },
    },
    dialogPositioning: {
      overflow: 'hidden',
      '& .MuiDialog-container': {
        '& .MuiDialogTitle-root': {
          paddingTop: 25,
          paddingBottom: 15,
          '& p, h2': {
            fontSize: 24,
            fontWeight: 'bold',
            letterSpacing: 'normal',
          },
        },
        '& .MuiDialogActions-root': {
          paddingBottom: 24,
          [theme.breakpoints.down('xs')]: {
            marginRight: 0,
          },
          marginRight: 8,
        },
        '& .MuiDialogTitle-root, .MuiDialogActions-root': {
          paddingLeft: 32,
          paddingRight: 32,
          [theme.breakpoints.down('xs')]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
          '& p': {
            fontSize: 15,
            letterSpacing: 'normal',
          },
        },
      },
      '& .MuiButton-root': {
        height: 40,
        textTransform: 'none',
        minWidth: 140,
        marginTop: 8,
        color: theme.palette.primary.darkText,
        [theme.breakpoints.down('xs')]: {
          height: 56,
        },
      },
    },
    closeIcon: {
      position: 'absolute',
      right: 32,
      top: 32,
      [theme.breakpoints.down('xs')]: {
        right: 16,
      },
      '&:hover': {
        cursor: 'pointer',
      },
    },
    dialogTitleRoot: {
      padding: '20px 0',
    },
  };
};

const DialogActionsWrapper = ({ onTouchMove, ...props }) => {
  const ref = useRef();
  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('touchmove', onTouchMove, { passive: false });
    }
    return () => {
      if (current) {
        current.removeEventListener('touchmove', onTouchMove, { passive: false });
      }
    };
  }, [onTouchMove]);
  return (
    <RootRef rootRef={ref}>
      <DialogActions {...props} />
    </RootRef>
  );
};

DialogActionsWrapper.propTypes = {
  onTouchMove: PropTypes.func,
};

class AmountPriceComponent extends React.Component {
  static propTypes = {
    boxed: PropTypes.bool,
    classes: PropTypes.object,
    amount: PropTypes.number,
    priceFormatted: PropTypes.string,
    vatNotice: PropTypes.string,
    productVariantComponent: PropTypes.elementType.isRequired,
    ConfigurationModeSwitchComponent: PropTypes.elementType.isRequired,
    showConfigurationModeSwitch: PropTypes.bool,
    error: PropTypes.object,
    width: PropTypes.string,
    hidePrices: PropTypes.bool,
    hidePieces: PropTypes.bool,
    priceList: PropTypes.array,
    showProductVariants: PropTypes.bool,
    title: PropTypes.string,
    showCallToAction: PropTypes.bool,
    showReceiveOfferForm: PropTypes.bool,
    onClose: PropTypes.func,
    sendReceiveOfferRequest: PropTypes.func,
    setConfigurationComment: PropTypes.func,
    onSelectableChanged: PropTypes.func,
    onAddToBasket: PropTypes.func,
    isBulkNamesSelected: PropTypes.bool,
    clientTexts: PropTypes.object,
    bulkSavings: PropTypes.array,
    notifications: PropTypes.array,
    displayAcceptDesign: PropTypes.bool,
    bulkNameErrors: PropTypes.array,
    onShowChange: PropTypes.func,
    wrapperClass: PropTypes.string,
    validateConfiguration: PropTypes.func,
    addToCartPopUpLoader: PropTypes.bool,
    summary: PropTypes.object,
    fetchSummary: PropTypes.func,
    selectedStage: PropTypes.string,
    stepToNextStage: PropTypes.func,
    loadConfiguration: PropTypes.func,
    designData: PropTypes.object,
    selectedDesign: PropTypes.string,
    setShowProductParts: PropTypes.func,
    showProductParts: PropTypes.bool,
    hasLoadCode: PropTypes.bool,
    isLoaded: PropTypes.bool,
    validateAnswers: PropTypes.func.isRequired,
  };

  static defaultProps = {
    showCallToAction: true,
    showConfigurationModeSwitch: false,
    notifications: [],
    clientTexts: {
      termsAndConditionsLink: '',
    },
    hidePieces: false,
  };

  state = {
    showPriceOverview: false,
    notice: '',
    hasErrorReceiveOfferForm: false,
    bulkNameErrors: [],
    approval: {
      value: false,
      error: null,
    },
    inView: true,
    showErrors: false,
    showFeedbackSuccessDialog: false,
  };

  checkboxContainer = React.createRef();

  componentDidUpdate(prevProps, prevState) {
    const { error, bulkNameErrors } = this.props;
    if (error && !isEqual(error, prevProps.error) && this.dialogContentRef.current) {
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (bulkNameErrors !== prevProps.bulkNameErrors) {
      this.setState({ bulkNameErrors });
    }
    if (prevProps.addToCartPopUpLoader && !this.props.addToCartPopUpLoader) {
      this.handleClose();
    }
    const { showPriceOverview } = this.state;
    if (showPriceOverview !== prevState.showPriceOverview) {
      const { onShowChange } = this.props;
      onShowChange && onShowChange(showPriceOverview);
    }
  }

  receiveOfferFormComponentRef = React.createRef();
  dialogContentRef = React.createRef();
  priceListRef = React.createRef();

  onNextClick = () => {
    const { selectedStage, stepToNextStage, loadConfiguration, hasLoadCode, isLoaded } = this.props;
    if (selectedStage === DESIGN && (!hasLoadCode || !isLoaded)) {
      loadConfiguration();
    }
    stepToNextStage();
  };

  handleChange = (event) => {
    this.setState({
      [event.target.name]: {
        value: event.target.checked,
        error: null,
      },
    });
  };

  verifyRequestErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if (bulkNameErrors && bulkNameErrors.length > 0) {
      this.setState({ showErrors: true });
      this.setState({ bulkNameErrors });
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (displayAcceptDesign && !designApproved) {
      this.setState({
        approval: {
          value: false,
          error: true,
        },
      });
      setTimeout(() => {
        this.checkboxContainer.current && this.checkboxContainer.current.scrollIntoView();
      }, 500);
    }
  };

  requestHasErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if ((bulkNameErrors && bulkNameErrors.length > 0) || (displayAcceptDesign && !designApproved)) {
      return true;
    }
    return false;
  };

  onAddToBasket = () => {
    const { onAddToBasket } = this.props;
    if (!this.requestHasErrors()) {
      onAddToBasket();
    } else {
      this.verifyRequestErrors();
    }
  };

  handleTouchMove = ((evt) => {
    const dialogContent = this.dialogContentRef.current;
    evt.preventDefault();
    dialogContent.scrollTo(0, dialogContent.scrollTop);
  }).bind(this);

  openPriceOverview = async () => {
    try {
      await this.props.validateConfiguration();
      this.setState({
        showPriceOverview: !this.state.showPriceOverview,
        hasErrorReceiveOfferForm: false,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  handleClose = () => {
    this.setState({
      showPriceOverview: false,
      bulkNameErrors: [],
      approval: {
        value: false,
        error: null,
      },
      showErrors: false,
    });
    this.props.onClose();
  };

  onReceiveOfferFormPress = async () => {
    const answerValidation = this.props.validateAnswers();
    const formValidation = this.receiveOfferFormComponentRef.current.validate();
    if (!this.requestHasErrors() && formValidation && answerValidation) {
      const result = await this.props.sendReceiveOfferRequest(formValidation);
      if (!result) {
        this.setState({ hasErrorReceiveOfferForm: true });
        this.dialogContentRef.current.scrollTo(0, 0);
      }
      if (result) {
        this.setState({ hasErrorReceiveOfferForm: false });
        this.openFeedbackSuccessDialog();
      }
    } else {
      this.verifyRequestErrors();
    }
  };

  handleBasketNoticeChange = (event) => {
    this.setState({ notice: event.target.value });
    this.props.setConfigurationComment(event.target.value);
  };

  onSelectableChanged = (priceData) => (key, newSelected) => {
    const nextData = priceData.map((currentElement) => {
      if (currentElement.key === key) {
        return {
          ...currentElement,
          selected: newSelected,
          price: newSelected ? '10,00 €' : '0,00 €',
        };
      }
      return currentElement;
    });
    this.setState({ priceData: nextData });
  };

  priceListClick = () => {};

  showPriceListButton = (inView) => {
    this.setState({ inView: inView });
  };

  closeFeedbackSuccessDialog = () =>
    this.setState((state) => ({ ...state, showFeedbackSuccessDialog: false }));

  openFeedbackSuccessDialog = () => this.setState((state) => ({ ...state, showFeedbackSuccessDialog: true }));

  renderPriceOverview() {
    const { notice, showPriceOverview, approval, inView, showErrors, bulkNameErrors } = this.state;
    const {
      amount,
      priceFormatted,
      showProductVariants,
      productVariantComponent,
      error,
      width,
      hidePrices,
      classes,
      showReceiveOfferForm,
      vatNotice,
      clientTexts,
      displayAcceptDesign,
      notifications,
      showCallToAction,
    } = this.props;

    const { termsAndConditionsLink } = isEmpty(clientTexts)
      ? { termsAndConditionsLink: 'https://www.autositzbezuege-rau.de/agb/' }
      : clientTexts;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: 'error',
          color: 'error',
        });
      });
    }

    return (
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{
          paper: classes.dialogWrapper,
          container: width === 'md' || width === 'xs' ? classes.dialogPositioning : '',
          root: classes.dialogPositioning,
        }}
        BackdropProps={{ className: classes.dialogPositioning }}
        onClose={this.handleClose.bind(this)}
        open={showPriceOverview}
        maxWidth={'lg'}
        fullWidth
        fullScreen={width === 'sm' || width === 'xs'}
        scroll="paper"
      >
        <LoadingOverlay show={this.props.addToCartPopUpLoader} />
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Typography variant="body1">
            <b>{t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}</b>
          </Typography>
          <Icon className={classes.closeIcon} onClick={this.handleClose.bind(this)}>
            close
          </Icon>
        </DialogTitle>
        <RootRef rootRef={this.dialogContentRef}>
          <DialogContent classes={{ root: classes.contentPositioning }}>
            {this.state.hasErrorReceiveOfferForm && (
              <Typography color="error" variant="body2">
                {t('receiveOfferForm.checkInputsInForm')}
              </Typography>
            )}
            <PriceOverview
              title={t('amountPrice.productVariants')}
              contactData={t('amountPrice.contactData')}
              handleClose={this.handleClose}
              acceptDesignComponent={
                <AcceptDesign
                  termsAndConditionsLink={termsAndConditionsLink}
                  displayAcceptDesign={displayAcceptDesign}
                  handleChangeCheckboxApproval={this.handleChange}
                  approval={approval}
                  closeProductOverviewDialog={this.handleClose}
                  notice={notice}
                  noteLabel={t('amountPrice.notice')}
                  handleChange={this.handleBasketNoticeChange}
                  containerRef={this.checkboxContainer}
                />
              }
              notificationComponent={mergedNotifications.map(({ message, level, color }, key) => {
                return <Notification key={key} icon={level} color={color} notification={message} />;
              })}
              priceListComponent={null}
              productVariantComponent={showProductVariants && productVariantComponent}
              receiveOfferFormComponent={
                <ReceiveOfferForm
                  innerRef={this.receiveOfferFormComponentRef}
                  notice={notice}
                  noteLabel={t('amountPrice.notice')}
                  handleChange={this.handleBasketNoticeChange}
                />
              }
              showReceiveOfferForm={showReceiveOfferForm}
            />
          </DialogContent>
        </RootRef>
        <DialogActionsWrapper onTouchMove={this.handleTouchMove}>
          <AddToCart
            buttonLabel={t('amountPrice.buy')}
            showCallToAction={showCallToAction}
            onPress={showReceiveOfferForm ? this.onReceiveOfferFormPress : this.onAddToBasket}
            handleChange={this.handleBasketNoticeChange}
            disabled={!!error}
            totalPrice={priceFormatted}
            taxNote={this.renderVatNotice()}
            showPriceList={!inView}
            priceListLabel={t('amountPrice.showPriceList')}
            priceListClick={this.priceListClick}
            hidePrices={hidePrices}
            amount={amount}
            showReceiveOfferForm={showReceiveOfferForm}
          />
        </DialogActionsWrapper>
      </Dialog>
    );
  }

  renderVatNotice() {
    return (
      <>
        {t('amountPrice.vatNoticeLine1')}
        <br />
        <i>{t('amountPrice.vatNoticeLine2')}</i>
      </>
    );
  }

  render() {
    const {
      classes,
      priceFormatted,
      selectedStage,
      designData,
      selectedDesign,
      setShowProductParts,
      showProductParts,
      width,
    } = this.props;

    const { showPriceOverview, showFeedbackSuccessDialog } = this.state;
    const isSmallScreen = width === 'xs';

    const showPrice = selectedStage === MATERIAL && (!isSmallScreen || (!showProductParts && isSmallScreen));

    return (
      <>
        {!isSmallScreen && <BackStepLink />}
        <div className={classes.root}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={8} className={classes.AmountPriceContent}>
              {isSmallScreen && <BackStepLink />}
              {showPrice && (
                <div className={classes.AmountPrice}>
                  <Typography
                    variant="h5"
                    classes={{
                      root: classes.AmountPriceTypography,
                    }}
                  >
                    <b>{priceFormatted}</b>
                  </Typography>
                  {priceFormatted && (
                    <Typography
                      className={classes.pos}
                      variant="caption"
                      classes={{
                        root: classes.AmountPriceTypographyVat,
                      }}
                    >
                      {this.renderVatNotice()}
                    </Typography>
                  )}
                </div>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {selectedStage === MATERIAL && isSmallScreen && (
                <Button
                  variant="contained"
                  fullWidth
                  size="medium"
                  classes={{
                    root: clsx(classes.mainButton, !showProductParts && classes.secondaryButton),
                    label: classes.mainButtonLabel,
                  }}
                  onClick={() => (showProductParts ? setShowProductParts(false) : setShowProductParts(true))}
                >
                  {showProductParts ? t('amountPrice.closeProductParts') : t('amountPrice.openProductParts')}
                </Button>
              )}
              {(selectedStage !== MATERIAL || !isSmallScreen || (!showProductParts && isSmallScreen)) && (
                <Button
                  variant="contained"
                  fullWidth
                  size="medium"
                  disabled={
                    selectedStage === PRODUCT
                      ? !designData
                      : selectedStage === DESIGN
                      ? !selectedDesign
                      : false
                  }
                  onClick={selectedStage === MATERIAL ? this.openPriceOverview : this.onNextClick}
                  classes={{
                    root: classes.mainButton,
                    label: classes.mainButtonLabel,
                  }}
                >
                  {selectedStage === MATERIAL
                    ? t('amountPrice.intoCart')
                    : selectedStage === PRODUCT
                    ? t('amountPrice.designNext')
                    : t('amountPrice.materialNext')}
                </Button>
              )}
            </Grid>
          </Grid>
        </div>
        {showPriceOverview && this.renderPriceOverview()}
        <FeedbackSuccessDialog open={showFeedbackSuccessDialog} onClose={this.closeFeedbackSuccessDialog} />
      </>
    );
  }
}

export default withWidth()(withStyles(styles, { name: 'AmountPrice' })(AmountPriceComponent));
